import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, RefreshCw, Layers, MapPinned, Thermometer, Wind } from 'lucide-react';
import { Badge, Dropdown } from '../ui';
import './MapControlsBar.css';

export function MapControlsBar({ farm, filters, onFilterChange, sidebarCollapsed }) {
  const { t } = useTranslation();
  const [layerMenuOpen, setLayerMenuOpen] = useState(false);
  
  const statusOptions = [
    { value: [], label: t('stations.filters.allStatuses') },
    { value: ['active'], label: t('stations.filters.activeOnly') },
    { value: ['warning'], label: t('stations.filters.warningOnly') },
    { value: ['inactive'], label: t('stations.filters.inactiveOnly') }
  ];
  
  const typeOptions = [
    { value: 'all', label: t('stations.filters.allTypes') },
    { value: 'campo_aperto', label: t('stations.types.campo_aperto') },
    { value: 'retro_modulo', label: t('stations.types.retro_modulo') }
  ];
  
  const layerOptions = [
    { id: 'satellite', label: t('map.layerOptions.satellite') },
    { id: 'road', label: t('map.layerOptions.road') },
    { id: 'cadastral', label: t('map.layerOptions.cadastral') }
  ];
  
  const [selectedLayer, setSelectedLayer] = useState('satellite');
  const [showAreaOverlay, setShowAreaOverlay] = useState(false);
  const [activeHeatmap, setActiveHeatmap] = useState(null);
  
  // Listen for layer changes from the map component
  useEffect(() => {
    const handleLayerChange = (e) => {
      setSelectedLayer(e.detail.layerId);
    };
    
    window.addEventListener('map:change-layer', handleLayerChange);
    return () => window.removeEventListener('map:change-layer', handleLayerChange);
  }, []);
  
  // Listen for heatmap changes from the map component to sync state
  useEffect(() => {
    const handleToggleHeatmap = (e) => {
      setActiveHeatmap(e.detail.heatmapType);
    };
    
    window.addEventListener('map:toggle-heatmap', handleToggleHeatmap);
    return () => window.removeEventListener('map:toggle-heatmap', handleToggleHeatmap);
  }, []);
  
  const handleRefresh = () => {
    window.location.reload();
  };
  
  const handleToggleAreaOverlay = () => {
    const newState = !showAreaOverlay;
    setShowAreaOverlay(newState);
    window.dispatchEvent(new CustomEvent('map:toggle-area-overlay', { 
      detail: { visible: newState } 
    }));
  };
  
  const handleToggleHeatmap = (heatmapType) => {
    const newActive = activeHeatmap === heatmapType ? null : heatmapType;
    setActiveHeatmap(newActive);
    window.dispatchEvent(new CustomEvent('map:toggle-heatmap', { 
      detail: { heatmapType: newActive } 
    }));
  };
  
  const heatmapOptions = [
    { id: 'temperature', label: t('map.heatmapTypes.temperature'), icon: Thermometer },
    { id: 'windSpeed', label: t('map.heatmapTypes.windSpeedAndDirection'), icon: Wind },
  ];
  
  const getStatusTriggerLabel = () => {
    if (!filters?.status || filters.status.length === 0) return t('stations.filters.allStatuses');
    if (filters.status.length === 1) {
      const s = filters.status[0];
      if (s === 'active') return t('stations.filters.activeOnly');
      if (s === 'warning') return t('stations.filters.warningOnly');
      if (s === 'inactive') return t('stations.filters.inactiveOnly');
      return t('common.selected');
    }
    return t('common.selectedCount', { count: filters.status.length });
  };

  return (
    <div className={`map-controls-bar ${sidebarCollapsed ? 'map-controls-bar--sidebar-collapsed' : ''}`}>
      {/* Status Filter */}
      <Dropdown
        trigger={
          <button className="map-controls-bar__filter" data-testid="status-filter">
            <span>
              {getStatusTriggerLabel()}
            </span>
            <ChevronDown size={16} />
          </button>
        }
        items={statusOptions.map(opt => ({
          label: opt.label,
          onClick: () => onFilterChange({ status: opt.value })
        }))}
        align="left"
      />
      
      {/* Type Filter */}
      <Dropdown
        trigger={
          <button className="map-controls-bar__filter" data-testid="type-filter">
            <span>
              {filters.stationType === 'all' ? t('stations.filters.allTypes') :
               filters.stationType === 'campo_aperto' ? t('stations.types.campo_aperto') :
               t('stations.types.retro_modulo')}
            </span>
            <ChevronDown size={16} />
          </button>
        }
        items={typeOptions.map(opt => ({
          label: opt.label,
          onClick: () => onFilterChange({ stationType: opt.value })
        }))}
        align="left"
      />
      
      <div className="map-controls-bar__spacer" />
      
      {/* Action Buttons */}
      <button 
        className="map-controls-bar__action"
        onClick={handleRefresh}
        title={t('map.controls.refreshData')}
        data-testid="refresh-btn"
      >
        <RefreshCw size={20} />
      </button>
      
      <button 
        className={`map-controls-bar__action ${showAreaOverlay ? 'map-controls-bar__action--active' : ''}`}
        onClick={handleToggleAreaOverlay}
        title={showAreaOverlay ? t('map.controls.hideAreaOverlay') : t('map.controls.showAreaOverlay')}
        data-testid="area-overlay-btn"
      >
        <MapPinned size={20} />
      </button>
      
      <Dropdown
        trigger={
          <button 
            className={`map-controls-bar__action ${activeHeatmap ? 'map-controls-bar__action--active' : ''}`}
            title={t('map.controls.heatmapOptions')}
            data-testid="heatmap-btn"
          >
            <Thermometer size={20} />
          </button>
        }
        items={heatmapOptions.map(opt => {
          const Icon = opt.icon;
          return {
            label: opt.label,
            icon: <Icon size={16} />,
            active: activeHeatmap === opt.id,
            onClick: () => handleToggleHeatmap(opt.id)
          };
        })}
        align="right"
        onOpen={() => {
          // Dispatch event to close popups when heatmap dropdown opens
          window.dispatchEvent(new CustomEvent('map:heatmap-dropdown-open'));
        }}
      />
      
      <Dropdown
        trigger={
          <button 
            className="map-controls-bar__action"
            title={t('map.controls.layerOptions')}
            data-testid="layers-btn"
          >
            <Layers size={20} />
          </button>
        }
        items={layerOptions.map(opt => ({
          label: opt.label,
          active: selectedLayer === opt.id,
          onClick: () => {
            setSelectedLayer(opt.id);
            window.dispatchEvent(new CustomEvent('map:change-layer', { 
              detail: { layerId: opt.id } 
            }));
          }
        }))}
        align="right"
        onOpen={() => {
          // Dispatch event to close station list panel when layers dropdown opens
          window.dispatchEvent(new CustomEvent('map:layers-dropdown-open'));
        }}
      />
    </div>
  );
}